import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import { Convenio, Hospital } from '@/types/_'
import { clearReservaDeSangue, getConfigObject } from '@/utils/common'
import Apis from '@/Apis/_'
import _ from 'lodash'
import { SolicitacaoCirurgiaImp } from '@/types/core'


@Module({ namespaced: true, name: 'ConfigSolicitacaoCirurgiaStore' })
export default class ConfigSolicitacaoCirurgiaStore extends VuexModule {
  private configSolicitacaoCirurgiaDefault: ConfigSolicitacaoCirurgiaDefault = this.createTemplateDefault()

  private mapaCirurgicoDefault: any = {
    agendamento: false,
    autorizacao: false,
    suprimentos: false,
    farmacia: false,
    centroCirurgico: false,
    centralDeMateriaisEspeciais: false,
    engenhariaClinica: false,
    preInternacao: false,
    opme: false,
    hotelaria: false,
    anestesia: false,
    gerenciamentoLeito: false,
    bancoDeSangue: false,
    tesouraria: false,
  }

  public hospitais: Hospital[] = []
  public hospitalId: string = ''
  public medicoSolicitanteId: string = 'cf038308-5e5f-4da2-a6d4-6849acfec17b'
  public configSolicitacaoCirurgia: ConfigSolicitacaoCirurgiaDefault = { ...this.configSolicitacaoCirurgiaDefault }
  public mapaCirurgico: any = { ...this.mapaCirurgicoDefault }
  public convenios: Convenio[] = []

  get solicitacaoCirurgia() {
    return this.configSolicitacaoCirurgia
  }

  solicitacaoCirurgiaImp: SolicitacaoCirurgiaImp.ISolicitacaoCirurgiaImp = new SolicitacaoCirurgiaImp.SolicitacaoCirurgiaImp()

  @Mutation
  setHospitais(value: Hospital[]) {
    this.hospitais = value
  }

  @Mutation
  setHospitalId(value: string) {
    this.hospitalId = value
  }

  @Mutation
  setMedicoSolicitanteId(value: string) {
    this.medicoSolicitanteId = value
  }

  @Mutation
  setConfigSolicitacaoCirurgia(value: any) {
    this.configSolicitacaoCirurgia = value
  }

  @Mutation
  setConfigMapaCirurgicoAreas(value: any[]) {
    this.mapaCirurgico = value
  }

  @Mutation
  clearFields(params: { field: string, val: any, template: ConfigSolicitacaoCirurgia }) {
    const { field, val, template } = params

    if (!val) {
      if (field === 'reservaDeSangue') {
        const reservaDeSangue = clearReservaDeSangue(template, val)
        Object.assign(template, reservaDeSangue)
      }

      Object.entries(template[field]).forEach(([key, value]) => {
        if ([
          'principal',
          'habilitado',
          'obrigatorio',
          'obrigatorioParaTodosOsConvenios',
          'obrigatorioParaConvenioParticular',
          'habilitarGrade',
          'gradeMedica',
          'lateralidadeEmSolicitacao',
          'lateralidadeEmProcedimento',
        ].includes(key)) {
          template[field][key] = val
        }

        if (key === 'rules') template[field][key] = []

        if (key === 'campos') {
          const { campos } = template[field]
          Object.entries(campos).forEach((obj: any) => { campos[obj[0]] = val })
        }
      })
    }
  }

  @Action
  forceCast<T>(obj: any) {
    return obj
  }

  @Action({ commit: 'setHospitais' })
  async getHospitais(userId: string) {
    const request = await Apis.Agendamento.SolicitacaoCirurgiaApi.getHospitaisByUserCurrent()
    if (request.success) {
      const data = request.data.map((c: any) => {
        const hospital = c
        hospital.id = _.toUpper(c.id)
        hospital.nome = _.toUpper(c.nome)
        return hospital
      })
      const hospitaisCast = await this.forceCast<Hospital[]>(data)
      return hospitaisCast
    }
    return []
  }

  @Action
  async saveRules() {
    // Create template by assurance
    const porConvenios = [] as any
    // eslint-disable-next-line no-restricted-syntax
    for (const elem of this.configSolicitacaoCirurgia.porConvenios) {
      const { convenioIds } = elem
      delete (elem as any).convenioIds
      delete (elem as any).porConvenios

      // eslint-disable-next-line no-await-in-loop
      const template = await this.templateFromFormToApi(elem)
      porConvenios.push({
        ...template,
        convenioIds,
      })
    }
    // Create main template
    const mainTemplate: any = await this.templateFromFormToApi(this.configSolicitacaoCirurgia)
    mainTemplate.template.porConvenios = porConvenios // Apend by assurance

    mainTemplate.template = JSON.stringify(mainTemplate.template, null, 2)

    const data: any = {
      ...mainTemplate,
      hospitalId: this.hospitalId,
    }

    const request = await Apis.Agendamento.SolicitacaoCirurgiaApi.updateModeloFormulario(data)
    return request
  }

  @Action
  async templateFromFormToApi(data: ConfigSolicitacaoCirurgiaDefault | ConfigSolicitacaoCirurgia) {
    const formTemplate: any = {}
    const formulario: any = {}
    const hospital: any = this.hospitais.find((e: Hospital) => e.id === this.hospitalId)

    Object.entries(data).forEach(([key, value]) => {
      const campo = (value as any)

      if (Array.isArray(campo)) return

      if (campo.rules.length > 0) campo.rules.splice(0, campo.rules.length)

      if (campo.obrigatorio && !['medicamentosEspeciais', 'medicacaoContinua', 'equipeMedica', 'opmeDoConsultorio', 'instrumentadorExterno', 'previsaoUsoQuimioterapico', 'internacaoDeVespera', 'recemNascido', 'reservaHemocomponentesSimples', 'reservaDeSangue', 'lateralidade'].includes(key)) campo.rules.push('obrigatorio')

      if (['lateralidade'].includes(key) && data[key].lateralidadeEmSolicitacao) campo.rules.push('obrigatorio')

      if (['medicamentosEspeciais', 'medicacaoContinua', 'equipeMedica', 'opmeDoConsultorio', 'instrumentadorExterno', 'previsaoUsoQuimioterapico', 'internacaoDeVespera', 'recemNascido', 'reservaDeSangue', 'reservaHemocomponentesSimples'].includes(key) && campo.obrigatorio) campo.rules.push('radio')

      if (['nomePaciente', 'justificativa'].includes(key)) campo.rules.push('minimo:5')

      if (['nomePaciente'].includes(key)) campo.rules.push('maximo:70')

      if (['regiaoDaCirurgia'].includes(key)) campo.rules.push('maximo:100')

      if (key === 'opmes' && ['', undefined, null].includes(campo.fornecedorLabel)) campo.fornecedorLabel = 'Fornecedor/Marca'

      if (key === 'opmes' && ['', undefined, null].includes(campo.fornecedorButtonLabel)) campo.fornecedorButtonLabel = 'Cadastrar fornecedores'

      if (key === 'cpf') campo.rules.push('validarCPF')

      if (key === 'telefone') campo.rules.push('validarTelefoneForm')

      if (key === 'celular') campo.rules.push('validarTelefone')

      if (key === 'email') campo.rules.push('validarEmailForm')

      if (key === 'numeroDoAtendimentoAoPacienteNoHospital' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Número de atendimento'

      if (key === 'justificativaTecnica' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Justificativa técnica'

      if (key === 'anexos' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Laudos'

      if (key === 'anexos' && ['', undefined, null].includes(campo.instrucao)) campo.instrucao = 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF'

      if (key === 'documentacaoPaciente' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Documentação do paciente'

      if (key === 'documentacaoPaciente' && ['', undefined, null].includes(campo.instrucao)) campo.instrucao = 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF'

      if (key === 'termoDeConsentimento' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Termo de consentimento'

      if (key === 'termoDeConsentimento' && ['', undefined, null].includes(campo.instrucao)) campo.instrucao = 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF'

      if (key === 'documentosDiversos' && ['', undefined, null].includes(campo.instrucao)) campo.instrucao = 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF'

      if (key === 'documentosDiversos' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Documentos diversos'

      if (key === 'opcaoDataPrevisto1' && ['', undefined, null].includes(campo.intervaloPadrao)) campo.intervaloPadrao = 15

      if (key === 'opcaoDataPrevisto1' && ['', undefined, null].includes(campo.mensagemAdicional)) campo.mensagemAdicional = ''

      if (key === 'opcaoDataPrevisto2' && ['', undefined, null].includes(campo.intervaloPadrao)) campo.intervaloPadrao = 15

      if (key === 'opcaoDataPrevisto3' && ['', undefined, null].includes(campo.intervaloPadrao)) campo.intervaloPadrao = 15

      if (key === 'carteirinha' && ['', undefined, null].includes(campo.tamanhoMaximoDeCaracteres)) campo.tamanhoMaximoDeCaracteres = 30

      if (key === 'caixaCirurgica' && ['', undefined, null].includes(campo.titulo)) campo.titulo = 'Caixas cirúrgicas'

      formulario[key] = value
    })

    formTemplate.hospital = hospital
    delete formTemplate.hospital.fazReservaDeSala
    formTemplate.formulario = formulario

    return { template: formTemplate, templateMapaCirurgico: this.mapaCirurgico }
  }

  @Action
  async getHospitalTemplateBackoffice(hospitalId: string) {
    try {
      const request = await Apis.Agendamento.SolicitacaoCirurgiaApi.getTemplateBackofficeHospital(hospitalId)
      const { formulario, porConvenios } = JSON.parse(request.data.template)

      const mergedConfigObj: any = getConfigObject({ ...this.configSolicitacaoCirurgiaDefault }, formulario)
      if (porConvenios) {
        mergedConfigObj.porConvenios = porConvenios.map((elem: any) => {
          const { convenioIds } = elem
          return {
            ...getConfigObject({ ...this.configSolicitacaoCirurgiaDefault }, elem.template.formulario),
            convenioIds,
          }
        })
      }

      this.context.commit('setConfigSolicitacaoCirurgia', request.success ? mergedConfigObj : this.configSolicitacaoCirurgiaDefault)
      this.context.commit('setConfigMapaCirurgicoAreas', request.success ? request.data.templateMapaCirurgico : { ...this.mapaCirurgicoDefault })

      return true
    } catch (error) {
      this.context.commit('setConfigSolicitacaoCirurgia', this.configSolicitacaoCirurgiaDefault)
      this.context.commit('setConfigMapaCirurgicoAreas', { ...this.mapaCirurgicoDefault })
      return false
    }
  }

  @Action
  async reset() {
    this.context.commit('setConfigSolicitacaoCirurgia', this.configSolicitacaoCirurgiaDefault)
    this.context.commit('setConfigMapaCirurgicoAreas', { ...this.mapaCirurgicoDefault })
    this.setHospitalId('')
  }

  @Action
  async updateHospitalTemplate() {
    const params: any = {
      hospitalId: this.hospitalId,
      template: JSON.stringify(this.configSolicitacaoCirurgia, null, 2),
    }

    try {
      const request = await Apis.Agendamento.SolicitacaoCirurgiaApi.updateModeloFormulario(params)
      if (request.success) return request.data
      return true
    } catch (e) {
      return false
    }
  }

  @Mutation
  setConvenios(value: Convenio[]) {
    this.convenios = value
  }

  @Mutation
  clearConvenios() {
    this.convenios = []
  }

  @Action
  async getConvenios(hospitalId: string) {
    try {
      const response = await this.solicitacaoCirurgiaImp.getAllConvenio(hospitalId)
      if (response.success) {
        this.context.commit('setConvenios', response.data)
      }
      return response
    } catch (error) {
      return []
    }
  }

  @Action
  async addTemplateByAssurance() {
    this.configSolicitacaoCirurgia.porConvenios.push(await this.createTemplateByAssurance())
  }

  @Action
  removeTemplateByAssurance(index: number) {
    this.configSolicitacaoCirurgia.porConvenios.splice(index, 1)
  }

  @Action
  async createTemplateByAssurance() {
    const templateDefault = await this.createTemplateDefault()
    const template: ConfigSolicitacaoCirurgiaPorConvenio = {
      ...templateDefault,
      convenioIds: [],
    }

    return template
  }

  @Action
  createTemplateDefault() {
    const template: ConfigSolicitacaoCirurgiaDefault = {
      nomePaciente: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      peso: {
        habilitado: false,
        obrigatorio: false,
        visivelSomentePacienteAte15Anos: false,
        rules: [],
      },
      altura: {
        habilitado: false,
        obrigatorio: false,
        visivelSomentePacienteAte15Anos: false,
        rules: [],
      },
      comorbidadesDoPaciente: {
        habilitado: false,
        obrigatorio: false,
        visivelSomentePaciente70Anos: false,
        campos: {
          historicoDeCardiopatiaHisquemica: false,
          historicoDeCardiopatiaHisquemicaEspecificar: false,
          historicoDeInsuficienciaCardiaca: false,
          historicoDeInsuficienciaCardiacaEspecificar: false,
          historicoDeDoencaVascularCerebral: false,
          historicoDeDoencaVascularCerebralEspecificar: false,
          diabetes: false,
          diabetesEspecificar: false,
          obesidade: false,
          obesidadeEspecificar: false,
          arritimiaCardiacaPorFibrilacaoAuricular: false,
          arritimiaCardiacaPorFibrilacaoAuricularEspecificar: false,
          pacientePossuiLiberacaDoClinicoOuCardiologista: false,
          pacientePossuiLiberacaDoClinicoOuCardiologistaEspecificar: false,
        },
        rules: [],
      },
      nomeSocial: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      numeroDoAtendimentoAoPacienteNoHospital: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Número de atendimento',
        rules: [],
      },
      justificativaTecnica: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Justificativa técnica',
        rules: [],
      },
      cpf: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        principal: false,
        rules: [],
      },
      rgh: {
        habilitado: false,
        obrigatorio: false,
        principal: false,
        rules: [],
      },
      documentacaoObrigatoriaDoPaciente: {
        habilitado: true,
        obrigatorio: false,
        rules: [],
      },
      passaporte: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      nacionalidade: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      dataNascimento: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      recemNascido: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      sexo: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      telefone: {
        habilitado: false,
        obrigatorio: false,
        ocultarParcialmente: false,
        rules: [],
      },
      celular: {
        habilitado: false,
        obrigatorio: false,
        ocultarParcialmente: false,
        rules: [],
      },
      email: {
        habilitado: false,
        obrigatorio: false,
        ocultarParcialmente: false,
        rules: [],
      },
      convenio: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      plano: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      categoria: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      possuiConvenioAssociado: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      convenioAssociado: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      planoAssociado: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      categoriaAssociada: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      tipoAcomodacao: {
        habilitado: false,
        obrigatorioParaTodosOsConvenios: false,
        obrigatorioParaConvenioParticular: false,
        rules: [],
      },
      carteirinha: {
        habilitado: false,
        obrigatorio: false,
        tamanhoMaximoDeCaracteres: 30,
        rules: [],
      },
      anexos: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Laudos',
        instrucao: 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF',
        rules: [],
      },
      documentosDiversos: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Documentos diversos',
        instrucao: 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF',
        rules: [],
      },
      termoDeConsentimento: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Termo de consentimento',
        instrucao: 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF',
        rules: [],
      },
      documentacaoPaciente: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Documentação do paciente',
        instrucao: 'Ao carregar arquivos, dê preferência para imagens pois o sistema criará posteriormente um resumo em PDF',
        rules: [],
      },
      operadoraSenhaProvisoria: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      gradeCirurgica: {
        habilitado: false,
        rules: [],
      },
      gradeDisponibilidade: {
        habilitado: false,
        rules: [],
      },
      testeCovid: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      reoperacao: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      marcapasso: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      comorbidade: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      medicacaoDapt: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      funcaoRenal: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      complexidade: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          baixa: false,
          media: false,
          alta: false,
        },
        rules: [],
      },
      origemPaciente: {
        habilitado: false,
        obrigatorio: false,
        ativoNasInformacoesAssistenciais: false,
        rules: [],
      },
      carater: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          eletivo: false,
          urgencia: false,
          emergencia: false,
          emergenciaEfetiva: false,
        },
        rules: [],
      },
      justificativa: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      justificativaDePrazo: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      tipoProcedimento: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          ambulatorial: false,
          internacao: false,
          hospitalDia: false,
        },
        rules: [],
      },
      tipoInternacao: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          clinica: false,
          cirurgica: false,
          obstetrica: false,
          pediatrica: false,
          psiquiatrica: false,
        },
        rules: [],
      },
      medicoExecutante: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      examesIntraOperatorios: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          anatomiaPatologica: false,
          anatomiaPatologicaHospitalOuParticular: false,
          anatomiaPatologicaParticularLocal: false,
          biobanco: false,
          biopsia: false,
          biopsiaHospitalOuParticular: false,
          biopsiaParticularLocal: false,
          broncoscopiaFlexivel: false,
          catho: false,
          cellSaver: false,
          colangiografia: false,
          colangiopancreatografiaRetrograda: false,
          colonoscopia: false,
          congelacaoBACCHI: false,
          congelacaoFilomenaMarinhoCarvalho: false,
          congelacaoIPC: false,
          congelacaoMultipat: false,
          congelacaoPathos: false,
          congelacaoFerdinando: false,
          congelacaoSalomao: false,
          congelacaoLoccus: false,
          congelacaoFleury: false,
          congelacaoEinstein: false,
          congelacaoBML: false,
          congelacaoCIPAC: false,
          congelacaoPathology: false,
          dimen: false,
          ecocardiograma: false,
          ecointraoperatório: false,
          ecotransesofagico: false,
          endoscopia: false,
          escleraUnicamp: false,
          examesLaboratoriais: false,
          fivmed: false,
          insite: false,
          mamografia: false,
          medicinaNuclear: false,
          monitorizacaoNeurofisiologica: false,
          nervoNIM: false,
          neuronavegacao: false,
          outros: false,
          outrosEspecificar: false,
          potencialEvocado: false,
          pth: false,
          radioterapia: false,
          ressonancia: false,
          rxScopia: false,
          semExameIntraoperatorio: false,
          tipagemSanguinea: false,
          tomografia: false,
          tomografiaParaEstereotaxia: false,
          transporte: false,
          udo: false,
          ultrassonografiaDeImagem: false,
          ultrassonografiaIntraoperatoria: false,
          ultrassonografiaIntraoperatoriaEquipeDoHospital: false,
          ultrassonografiaLaparoscopio: false,
          ultrassonografiaRadiologia: false,
          ultrassonografiaRadiologiaAcompanhamento: false,
          culturaSonicacao: false,
          anestesiaAMD: false,
          exameEcoIntraoperatorio: false,
          exameUsgIntraoperatorio: false,
          gdlReservaDeUtiAdulto: false,
          gdlReservaDeUtiPediatrica: false,
          gdlUiHemoAngio: false,
          laboratorioPthRapido: false,
          servicoAnextesia: false,
          laboratorioBacchi: false,
          laboratorioFilomenaMarinoCarvalho: false,
          laboratorioPathosDiagnosticosMedicos: false,
          laboratorioLocus: false,
          laboratorioFleury: false,
          anestesiaGaap: false,
          broncoscopista: false,
          exameMamografia: false,
          exameRxIntraoperatorio: false,
          reservaDeRoll: false,
        },
        rules: [],
      },
      reservaHemocomponentesSimples: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          reservaHemocomponentesSimplesSim: false,
          reservaHemocomponentesSimplesNao: false,
        },
        rules: [],
      },
      reservaUti: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          reservaUtiSim: false,
          reservaUtiNao: false,
          geral: false,
          cardiologia: false,
          neoNatal: false,
          gdlUiHemoAngio: false,
          pediatrica: false,
          adulto: false,
          neuro: false,
          cardio: false,
          semiIntensiva: false,
          neurologica: false,
          semiIntensivaInfantil: false,
          semReservaUti: false,
        },
        rules: [],
      },
      anestesiaHospital: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        campos: {
          anestesiaSim: false,
          anestesiaNao: false,
          naoAplica: false,
          anestesistaDoHospitalNome: false,
          anestesistaDoHospitalCrm: false,
          anestesistaLista: false,
          sempreSelecionarAnestesistaNaLista: false,
          selecaoOpcionalCasoSim: false,
          selecaoOpcionalCasoNao: false,
          hospitalUtilizaClinicasDeAnestesia: false,
        },
        rules: [],
      },
      honorarioAnestesista: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          convenio: false,
          particular: false,
          consultorio: false,
          hospital: false,
          associacaoMedica: false,
          honorarioAnestesistaDocumento: false,
        },
        rules: [],
      },
      tipoAnestesia: {
        habilitado: false,
        obrigatorio: false,
        multiSelecao: false,
        campos: {
          bloqueiosEspinhais: false,
          geral: false,
          local: false,
          localComSedacao: false,
          plexular: false,
          raqui: false,
          sedacao: false,
          topica: false,
          outros: false,
        },
        rules: [],
      },
      alergiaLatex: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      alergiaContraste: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      pesoDoPaciente: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          maiorQue180Quilos: false,
          menorQue2Quilos: false,
          naoSeAplica: false,
        },
        rules: [],
      },
      farmacia: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      caixaCirurgica: {
        habilitado: false,
        obrigatorio: false,
        titulo: 'Caixas cirúrgicas',
        rules: [],
      },
      tempoDeDoenca: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          anos: false,
          meses: false,
          dias: false,
        },
        rules: [],
      },
      medicamentosEspeciais: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          medicamentosEspeciaisSim: false,
          medicamentosEspeciaisNao: false,
          medicamentosEspeciaisEspecificar: false,
        },
        rules: [],
      },
      medicacaoContinua: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          medicacaoContinuaSim: false,
          medicacaoContinuaNao: false,
          medicacaoContinuaEspecificar: false,
        },
        rules: [],
      },
      equipeMedica: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          dadosDoConselho: false,
          equipeMedicaEspecificar: false,
        },
        rules: [],
      },
      opmeDoConsultorio: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        campos: {
          opmeDoConsultorioSim: false,
          opmeDoConsultorioNao: false,
          opmeDoConsultorioEspecificar: false,
        },
        rules: [],
      },
      previsaoUsoQuimioterapico: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          previsaoUsoQuimioterapicoSim: false,
          previsaoUsoQuimioterapicoNao: false,
          previsaoUsoQuimioterapicoEspecificar: false,
        },
        rules: [],
      },
      hospitalInformaPacienteInternado: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      pacienteB24Positivo: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      pacienteEhTestemunhaDeJeova: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      internacaoDeVespera: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          internacaoDeVesperaSim: false,
          internacaoDeVesperaNao: false,
          internacaoDeVesperaEspecificar: false,
        },
        rules: [],
      },
      precaucoes: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          precaucoesSim: false,
          precaucoesNao: false,
          precaucoesRespiratoriaGoticulas: false,
          precaucoesRespiratoriaAerosol: false,
          precaucoesPorContatoRespiratoriaAerosol: false,
          precaucoesEspecial: false,
          precaucoesOutro: false,
          precaucoesEspecificar: false,
        },
        rules: [],
      },
      medicoConvidado: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          medicoConvidadoSim: false,
          medicoConvidadoNao: false,
          medicoConvidadoNome: false,
          medicoConvidadoCrm: false,
        },
        rules: [],
      },
      honorarioMedico: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          particular: false,
          consultorio: false,
          hospital: false,
          codigoPrestador: false,
          associacaoMedica: false,
          honorarioMedicoDocumento: false,
        },
        rules: [],
      },
      instrumentalAdicional: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      equipamentos: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      cids: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      procedimentoPrincipal: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          opcaoParticular: false,
        },
        rules: [],
      },
      procedimentosAssociados: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          opcaoParticular: false,
        },
        rules: [],
      },
      opmes: {
        habilitado: false,
        obrigatorio: false,
        habilitarGrade: false,
        campos: {
          opcaoParticular: false,
          fornecedor1Id: true,
          fornecedor2Id: true,
          fornecedor3Id: true,
        },
        fornecedor1Id: {
          obrigatorio: true,
          permiteNaoInformado: true,
          verificaFornecedorNaoHomologadoPeloHospital: false,
        },
        fornecedor2Id: {
          obrigatorio: false,
          permiteNaoInformado: false,
          verificaFornecedorNaoHomologadoPeloHospital: false,
        },
        fornecedor3Id: {
          obrigatorio: false,
          permiteNaoInformado: false,
          verificaFornecedorNaoHomologadoPeloHospital: false,
        },
        fornecedorLabel: 'Fornecedor/Marca',
        fornecedorButtonLabel: 'Cadastrar fornecedores',
        medidaTamanho: {
          habilitado: false,
          obrigatorio: false,
        },
        rules: [],
      },
      tempoCirurgia: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      diaria: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      diariaUti: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      tecnicaDaCirurgia: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          robotica: false,
          video: false,
          convencional: false,
        },
        rules: [],
      },
      instrucaoDataPrevista: {
        habilitado: false,
        habilitadoOrcamentoParticularAvulso: false,
        obrigatorio: false,
        rules: [],
      },
      orientacaoPreparoPaciente: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      reservaHemocomponentes: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          concentradoHemacias: false,
          concentradoPlaquetas: false,
          crioprecipitado: false,
          plasmaFrescoCongelado: false,
          tipagemSanguinea: false,
          tipagemSanguineaABORHPAI: false,
          semReserva: false,
        },
        rules: [],
      },
      dataAgendamentoVirtual: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      opcaoDataPrevisto1: {
        habilitado: false,
        obrigatorio: false,
        gradeMedica: false,
        intervaloPadrao: 15,
        mensagemAdicional: '',
        rules: [],
      },
      opcaoDataPrevisto2: {
        habilitado: false,
        obrigatorio: false,
        intervaloPadrao: 15,
        rules: [],
      },
      opcaoDataPrevisto3: {
        habilitado: false,
        obrigatorio: false,
        intervaloPadrao: 15,
        rules: [],
      },
      lateralidade: {
        habilitado: false,
        obrigatorio: false,
        lateralidadeEmSolicitacao: false,
        lateralidadeEmProcedimento: true,
        exigeLateralidadeEmTodosOsProcedimentos: true,
        campos: {
          esquerdo: false,
          direito: false,
          bilateral: false,
          naoAplica: false,
        },
        rules: [],
      },
      diferencaDeAcomodacao: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      instrumentadorExterno: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          instrumentadorExternoSim: false,
          instrumentadorExternoNao: false,
          instrumentadorExternoEspecificar: false,
        },
        rules: [],
      },
      registraProtocoloDeRecuperacaoPrecoce: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      classificacaoCirurgia: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          limpa: false,
          potencialmenteContaminada: false,
          contaminada: false,
          infectada: false,
        },
        rules: [],
      },
      reservaDeSangue: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsNomePaciente: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsCpf: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsDataNascimento: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsSexo: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsNomeMae: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsUltimaTransfusao: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsDataCirurgia: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsReacaoTransfusional: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          rsReacaoTransfusionalSim: false,
          rsReacaoTransfusionalNao: false,
          rsReacaoTransfusionalEspecificar: false,
        },
        rules: [],
      },
      rsGestacaoPrevia: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsAbortoPrevio: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsPacienteTransplantado: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsPeso: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsHT: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsHB: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsINR: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsTTPA: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsFibrinogenio: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsPlaquetas: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsDataExame: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      rsConcentradoHemacias: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          rsConcentradoHemaciasSangramento: false,
          rsConcentradoHemaciasEspecificar: false,
          rsConcentradoHemaciasNaoAplica: false,
          rsConcentradoHemaciasFiltrado: false,
          rsConcentradoHemaciasFiltradoEIrradiado: false,
        },
        rules: [],
      },
      rsConcentradoPlaquetas: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          rsConcentradoPlaquetasAlteracaoFuncionalCirculacao: false,
          rsConcentradoPlaquetasAlteracaoFuncionalAnteagregante: false,
          rsConcentradoPlaquetasProcedimentosInvasivos: false,
          rsConcentradoPlaquetasCirurgiaPequenoPorte: false,
          rsConcentradoPlaquetasCirurgiaMedioPorte: false,
          rsConcentradoPlaquetasCirurgiaNeurologica: false,
          rsConcentradoPlaquetasEspecificar: false,
          rsConcentradoPlaquetasNaoAplica: false,
          rsConcentradoPlaquetasFiltrado: false,
          rsConcentradoPlaquetasFiltradoEIrradiado: false,
        },
        rules: [],
      },
      rsPlasmaFrescoCongelado: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          rsPlasmaFrescoCongeladoCoagulopatia: false,
          rsPlasmaFrescoCongeladoEspecificar: false,
          rsPlasmaFrescoCongeladoNaoAplica: false,
        },
        rules: [],
      },
      rsCrioprecipitado: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          rsCrioprecipitadoCoagulopatia: false,
          rsCrioprecipitadoEspecificar: false,
          rsCrioprecipitadoNaoAplica: false,
        },
        rules: [],
      },
      mapaCirurgicoArea: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          areas: [],
        },
        rules: [],
      },
      suprimentosCotacao: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          naoExigeAnexoCotacao: false,
          flagCotacaoDefault: false,
          flagCotacaoParticularDefault: false,
          naoExigeValorDePacote: false,
        },
        rules: [],
      },
      alterarSolicitacao: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          naoEditarProcedimentoPrincipal: false,
          naoEditarProcedimentosAssociados: false,
          naoEditarObservacaoReservaSala: false,
          naoEditarOpme: false,
          naoEditarEquipamentos: false,
          naoEditarTipoInternacao: false,
          naoEditarAnestesita: false,
          naoEditarEquipeMedica: false,
          naoEditarHonorarioAnestesista: false,
          naoEditarHonorarioMedico: false,
          naoEditarReservaHemocomponentes: false,
        },
        rules: [],
      },
      guiaDeAutorizacao: {
        campos: {
          Cancelada: false,
          EmAnalise: false,
          AutorizadaParcial: false,
          AutorizadaAceitaMedico: false,
          Autorizada: false,
        },
        rules: [],
      },
      porConvenios: [],
      observacaoDoCancelamento: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      centroCirurgico: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      tipoCentroCirurgico: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          Geral: false,
          Ambulatorial: false,
          HospitalDia: false,
          Hemodinamica: false,
          Radioterapia: false,
          SalaDePequenosProcedimentos: false,
        },
        rules: [],
      },
      naoExibeMensagemTempoAutorizacaoPorTipoCentroCirurgico: {
        habilitado: false,
        campos: {
          Geral: false,
          Ambulatorial: false,
          HospitalDia: false,
          Hemodinamica: false,
          Radioterapia: false,
          SalaDePequenosProcedimentos: false,
        },
        rules: [],
      },
      regiaoDaCirurgia: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      justificativaDoProtocolo: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
      formularioMedico: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          limparReservaUtiNoTipoDeInternacaoAmbulatorial: false,
        },
        rules: [],
      },
      visualizarAgendamentoMedico: {
        habilitado: false,
        obrigatorio: false,
        campos: {
          permitirVisualizarAgendamentosDoMedicoNaTriagem: false,
        },
        rules: [],
      },
      anexo: {
        rules: [],
        acoes: {
          permiteGerarGuiaTissSadt: false,
        },
      },
      processarArquivoAnexo: {
        habilitado: false,
        campos: {
          processarImagem: false,
          processarPdf: false,
        },
        rules: [],
      },
      horasDeAntecedenciaDeInternacao: {
        habilitado: false,
        obrigatorio: false,
        rules: [],
      },
    }

    return template
  }
}

export interface ConfigSolicitacaoCirurgiaDefault extends ConfigSolicitacaoCirurgia {
  porConvenios: ConfigSolicitacaoCirurgiaPorConvenio[]
}

export interface ConfigSolicitacaoCirurgiaPorConvenio extends ConfigSolicitacaoCirurgia {
  convenioIds: string[]
}

export interface ConfigSolicitacaoCirurgia {
  nomePaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  peso: {
    habilitado: boolean,
    obrigatorio: boolean,
    visivelSomentePacienteAte15Anos: boolean,
    rules: any[],
  },
  altura: {
    habilitado: boolean,
    obrigatorio: boolean,
    visivelSomentePacienteAte15Anos: boolean,
    rules: any[],
  },
  comorbidadesDoPaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    visivelSomentePaciente70Anos: boolean,
    campos: {
      historicoDeCardiopatiaHisquemica: boolean,
      historicoDeCardiopatiaHisquemicaEspecificar: boolean,
      historicoDeInsuficienciaCardiaca: boolean,
      historicoDeInsuficienciaCardiacaEspecificar: boolean,
      historicoDeDoencaVascularCerebral: boolean,
      historicoDeDoencaVascularCerebralEspecificar: boolean,
      diabetes: boolean,
      diabetesEspecificar: boolean,
      obesidade: boolean,
      obesidadeEspecificar: boolean,
      arritimiaCardiacaPorFibrilacaoAuricular: boolean,
      arritimiaCardiacaPorFibrilacaoAuricularEspecificar: boolean,
      pacientePossuiLiberacaDoClinicoOuCardiologista: boolean,
      pacientePossuiLiberacaDoClinicoOuCardiologistaEspecificar: boolean,
    },
    rules: any[],
  },
  nomeSocial: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  numeroDoAtendimentoAoPacienteNoHospital: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    rules: any[],
  },
  justificativaTecnica: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    rules: any[],
  },
  cpf: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    principal: boolean,
    rules: any[],
  },
  rgh: {
    habilitado: boolean,
    obrigatorio: boolean,
    principal: boolean,
    rules: any[],
  },
  documentacaoObrigatoriaDoPaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  passaporte: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: [],
  },
  nacionalidade: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: [],
  },
  dataNascimento: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  recemNascido: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  sexo: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  telefone: {
    habilitado: boolean,
    obrigatorio: boolean,
    ocultarParcialmente: boolean,
    rules: any[],
  },
  celular: {
    habilitado: boolean,
    obrigatorio: boolean,
    ocultarParcialmente: boolean,
    rules: any[],
  },
  email: {
    habilitado: boolean,
    obrigatorio: boolean,
    ocultarParcialmente: boolean,
    rules: any[],
  },
  convenio: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  plano: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  categoria: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  possuiConvenioAssociado: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  }
  convenioAssociado: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  planoAssociado: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  categoriaAssociada: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  tipoAcomodacao: {
    habilitado: boolean,
    obrigatorioParaTodosOsConvenios: boolean,
    obrigatorioParaConvenioParticular: boolean,
    rules: any[],
  },
  carteirinha: {
    habilitado: boolean,
    obrigatorio: boolean,
    tamanhoMaximoDeCaracteres: number | string,
    rules: any[],
  },
  anexos: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    instrucao: string,
    rules: any[],
  },
  documentosDiversos: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    instrucao: string,
    rules: any[],
  },
  termoDeConsentimento: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    instrucao: string,
    rules: any[],
  },
  documentacaoPaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    instrucao: string,
    rules: any[],
  },
  operadoraSenhaProvisoria: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  gradeCirurgica: {
    habilitado: boolean,
    rules: any[],
  },
  gradeDisponibilidade: {
    habilitado: boolean,
    rules: any[],
  },
  testeCovid: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  reoperacao: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  marcapasso: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  comorbidade: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  medicacaoDapt: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  funcaoRenal: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  complexidade: {
    habilitado: false,
    obrigatorio: false,
    campos: {
      baixa: false,
      media: false,
      alta: false,
    },
    rules: [],
  },
  origemPaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    ativoNasInformacoesAssistenciais: boolean,
    rules: any[],
  },
  carater: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      eletivo: boolean,
      urgencia: boolean,
      emergencia: boolean,
      emergenciaEfetiva: boolean,
    },
    rules: any[],
  },
  justificativa: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  justificativaDePrazo: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  tipoProcedimento: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      ambulatorial: boolean,
      internacao: boolean,
      hospitalDia: boolean,
    },
    rules: any[],
  },
  tipoInternacao: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      clinica: boolean,
      cirurgica: boolean,
      obstetrica: boolean,
      pediatrica: boolean,
      psiquiatrica: boolean,
    },
    rules: any[],
  },
  medicoExecutante: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  examesIntraOperatorios: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      anatomiaPatologica: boolean,
      anatomiaPatologicaHospitalOuParticular: boolean,
      anatomiaPatologicaParticularLocal: boolean,
      biobanco: boolean,
      biopsia: boolean,
      biopsiaHospitalOuParticular: boolean,
      biopsiaParticularLocal: boolean,
      broncoscopiaFlexivel: boolean,
      catho: boolean,
      cellSaver: boolean,
      colangiografia: boolean,
      colangiopancreatografiaRetrograda: boolean,
      colonoscopia: boolean,
      congelacaoBACCHI: boolean,
      congelacaoFilomenaMarinhoCarvalho: boolean,
      congelacaoIPC: boolean,
      congelacaoMultipat: boolean,
      congelacaoPathos: boolean,
      congelacaoFerdinando: boolean,
      congelacaoSalomao: boolean,
      congelacaoLoccus: boolean,
      congelacaoFleury: boolean,
      congelacaoEinstein: boolean,
      congelacaoBML: boolean,
      congelacaoCIPAC: boolean,
      congelacaoPathology: boolean,
      dimen: boolean,
      ecocardiograma: boolean,
      ecointraoperatório: boolean,
      ecotransesofagico: boolean,
      endoscopia: boolean,
      escleraUnicamp: boolean,
      examesLaboratoriais: boolean,
      fivmed: boolean,
      insite: boolean,
      mamografia: boolean,
      medicinaNuclear: boolean,
      monitorizacaoNeurofisiologica: boolean,
      nervoNIM: boolean,
      neuronavegacao: boolean,
      outros: boolean,
      outrosEspecificar: boolean,
      potencialEvocado: boolean,
      pth: boolean,
      radioterapia: boolean,
      ressonancia: boolean,
      rxScopia: boolean,
      semExameIntraoperatorio: boolean,
      tipagemSanguinea: boolean,
      tomografia: boolean,
      tomografiaParaEstereotaxia: boolean,
      transporte: boolean,
      udo: boolean,
      ultrassonografiaDeImagem: boolean,
      ultrassonografiaIntraoperatoria: boolean,
      ultrassonografiaIntraoperatoriaEquipeDoHospital: boolean,
      ultrassonografiaLaparoscopio: boolean,
      ultrassonografiaRadiologia: boolean,
      ultrassonografiaRadiologiaAcompanhamento: boolean,
      culturaSonicacao: boolean,
      anestesiaAMD: boolean,
      exameEcoIntraoperatorio: boolean,
      exameUsgIntraoperatorio: boolean,
      gdlReservaDeUtiAdulto: boolean,
      gdlReservaDeUtiPediatrica: boolean,
      gdlUiHemoAngio: boolean,
      laboratorioPthRapido: boolean,
      servicoAnextesia: boolean,
      laboratorioBacchi: boolean,
      laboratorioFilomenaMarinoCarvalho: boolean,
      laboratorioPathosDiagnosticosMedicos: boolean,
      laboratorioLocus: boolean,
      laboratorioFleury: boolean,
      anestesiaGaap: boolean,
      broncoscopista: boolean,
      exameMamografia: boolean,
      exameRxIntraoperatorio: boolean,
      reservaDeRoll: boolean,
    },
    rules: any[],
  },
  reservaHemocomponentesSimples: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      reservaHemocomponentesSimplesSim: boolean,
      reservaHemocomponentesSimplesNao: boolean,
    },
    rules: any[],
  },
  reservaUti: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      reservaUtiSim: boolean,
      reservaUtiNao: boolean,
      geral: boolean,
      cardiologia: boolean,
      neoNatal: boolean,
      gdlUiHemoAngio: boolean,
      pediatrica: boolean,
      adulto: boolean,
      neuro: boolean,
      cardio: boolean,
      semiIntensiva: boolean,
      neurologica: boolean,
      semiIntensivaInfantil: boolean,
      semReservaUti: boolean,
    },
    rules: any[],
  },
  anestesiaHospital: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    campos: {
      anestesiaSim: boolean,
      anestesiaNao: boolean,
      naoAplica: boolean,
      anestesistaDoHospitalNome: boolean,
      anestesistaDoHospitalCrm: boolean,
      anestesistaLista: boolean,
      sempreSelecionarAnestesistaNaLista: boolean,
      selecaoOpcionalCasoSim: boolean,
      selecaoOpcionalCasoNao: boolean,
      hospitalUtilizaClinicasDeAnestesia: boolean,
    },
    rules: any[],
  },
  honorarioAnestesista: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      convenio: boolean,
      particular: boolean,
      consultorio: boolean,
      hospital: boolean,
      associacaoMedica: boolean,
      honorarioAnestesistaDocumento: boolean,
    },
    rules: any[],
  },
  tipoAnestesia: {
    habilitado: boolean,
    obrigatorio: boolean,
    multiSelecao: boolean,
    campos: {
      bloqueiosEspinhais: boolean,
      geral: boolean,
      local: boolean,
      localComSedacao: boolean,
      plexular: boolean,
      raqui: boolean,
      sedacao: boolean,
      topica: boolean,
      outros: boolean
    },
    rules: any[],
  },
  alergiaLatex: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  alergiaContraste: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  pesoDoPaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      maiorQue180Quilos: boolean,
      menorQue2Quilos: boolean,
      naoSeAplica: boolean,
    },
    rules: any[],
  },
  farmacia: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  caixaCirurgica: {
    habilitado: boolean,
    obrigatorio: boolean,
    titulo: string,
    rules: any[],
  },
  tempoDeDoenca: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      anos: boolean,
      meses: boolean,
      dias: boolean,
    },
    rules: any[],
  },
  medicamentosEspeciais: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      medicamentosEspeciaisSim: boolean,
      medicamentosEspeciaisNao: boolean,
      medicamentosEspeciaisEspecificar: boolean,
    },
    rules: any[],
  },
  medicacaoContinua: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      medicacaoContinuaSim: boolean,
      medicacaoContinuaNao: boolean,
      medicacaoContinuaEspecificar: boolean,
    },
    rules: any[],
  },
  equipeMedica: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      dadosDoConselho: boolean,
      equipeMedicaEspecificar: boolean,
    },
    rules: any[],
  },
  opmeDoConsultorio: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    campos: {
      opmeDoConsultorioSim: boolean,
      opmeDoConsultorioNao: boolean,
      opmeDoConsultorioEspecificar: boolean,
    },
    rules: any[],
  },
  previsaoUsoQuimioterapico: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      previsaoUsoQuimioterapicoSim: boolean,
      previsaoUsoQuimioterapicoNao: boolean,
      previsaoUsoQuimioterapicoEspecificar: boolean,
    },
    rules: any[],
  },
  hospitalInformaPacienteInternado: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  pacienteB24Positivo: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  pacienteEhTestemunhaDeJeova: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  internacaoDeVespera: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      internacaoDeVesperaSim: boolean,
      internacaoDeVesperaNao: boolean,
      internacaoDeVesperaEspecificar: boolean,
    },
    rules: any[],
  },
  precaucoes: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      precaucoesSim: boolean,
      precaucoesNao: boolean,
      precaucoesRespiratoriaGoticulas: boolean,
      precaucoesRespiratoriaAerosol: boolean,
      precaucoesPorContatoRespiratoriaAerosol: boolean,
      precaucoesEspecial: boolean,
      precaucoesOutro: boolean,
      precaucoesEspecificar: boolean,
    },
    rules: any[],
  },
  medicoConvidado: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      medicoConvidadoSim: boolean,
      medicoConvidadoNao: boolean,
      medicoConvidadoNome: boolean,
      medicoConvidadoCrm: boolean,
    },
    rules: any[],
  },
  honorarioMedico: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      particular: boolean,
      consultorio: boolean,
      hospital: boolean,
      codigoPrestador: boolean,
      associacaoMedica: boolean,
      honorarioMedicoDocumento: boolean,
    },
    rules: any[],
  },
  instrumentalAdicional: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  equipamentos: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  cids: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  procedimentoPrincipal: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      opcaoParticular: boolean,
    },
    rules: any[],
  },
  procedimentosAssociados: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      opcaoParticular: boolean,
    },
    rules: any[],
  },
  opmes: {
    habilitado: boolean,
    obrigatorio: boolean,
    habilitarGrade: boolean,
    campos: {
      opcaoParticular: boolean,
      fornecedor1Id: boolean,
      fornecedor2Id: boolean,
      fornecedor3Id: boolean,
    },
    fornecedor1Id: {
      obrigatorio: boolean,
      permiteNaoInformado: boolean,
      verificaFornecedorNaoHomologadoPeloHospital: boolean,
    },
    fornecedor2Id: {
      obrigatorio: boolean,
      permiteNaoInformado: boolean,
      verificaFornecedorNaoHomologadoPeloHospital: boolean,
    },
    fornecedor3Id: {
      obrigatorio: boolean,
      permiteNaoInformado: boolean,
      verificaFornecedorNaoHomologadoPeloHospital: boolean,
    },
    medidaTamanho: {
      habilitado: boolean,
      obrigatorio: boolean,
    },
    fornecedorLabel: string,
    fornecedorButtonLabel: string,
    rules: any[],
  },
  tempoCirurgia: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  diaria: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  diariaUti: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  tecnicaDaCirurgia: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      robotica: boolean,
      video: boolean,
      convencional: boolean,
    },
    rules: any[],
  },
  instrucaoDataPrevista: {
    habilitado: boolean,
    habilitadoOrcamentoParticularAvulso: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  orientacaoPreparoPaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  reservaHemocomponentes: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      concentradoHemacias: boolean,
      concentradoPlaquetas: boolean,
      crioprecipitado: boolean,
      plasmaFrescoCongelado: boolean,
      tipagemSanguinea: boolean,
      tipagemSanguineaABORHPAI: boolean,
      semReserva: boolean,
    },
    rules: any[],
  },
  dataAgendamentoVirtual: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  opcaoDataPrevisto1: {
    habilitado: boolean,
    obrigatorio: boolean,
    gradeMedica: boolean,
    intervaloPadrao: number | string,
    mensagemAdicional: string,
    rules: any[],
  },
  opcaoDataPrevisto2: {
    habilitado: boolean,
    obrigatorio: boolean,
    intervaloPadrao: number | string,
    rules: any[],
  },
  opcaoDataPrevisto3: {
    habilitado: boolean,
    obrigatorio: boolean,
    intervaloPadrao: number | string,
    rules: any[],
  },
  lateralidade: {
    habilitado: boolean,
    obrigatorio: boolean,
    lateralidadeEmSolicitacao: boolean,
    lateralidadeEmProcedimento: boolean,
    exigeLateralidadeEmTodosOsProcedimentos: boolean,
    campos: {
      esquerdo: boolean,
      direito: boolean,
      bilateral: boolean,
      naoAplica: boolean,
    },
    rules: any[],
  },
  diferencaDeAcomodacao: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  instrumentadorExterno: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      instrumentadorExternoSim: boolean,
      instrumentadorExternoNao: boolean,
      instrumentadorExternoEspecificar: boolean,
    },
    rules: any[],
  },
  registraProtocoloDeRecuperacaoPrecoce: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  classificacaoCirurgia: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      limpa: boolean,
      potencialmenteContaminada: boolean,
      contaminada: boolean,
      infectada: boolean,
    },
    rules: any[],
  },
  reservaDeSangue: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsNomePaciente: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsCpf: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsDataNascimento: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsSexo: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsNomeMae: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsUltimaTransfusao: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsDataCirurgia: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsReacaoTransfusional: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      rsReacaoTransfusionalSim: boolean,
      rsReacaoTransfusionalNao: boolean,
      rsReacaoTransfusionalEspecificar: boolean,
    },
    rules: any[],
  },
  rsGestacaoPrevia: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsAbortoPrevio: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsPacienteTransplantado: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsPeso: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsHT: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsHB: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsINR: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsTTPA: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsFibrinogenio: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsPlaquetas: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsDataExame: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  rsConcentradoHemacias: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      rsConcentradoHemaciasSangramento: boolean,
      rsConcentradoHemaciasEspecificar: boolean,
      rsConcentradoHemaciasNaoAplica: boolean,
      rsConcentradoHemaciasFiltrado: boolean,
      rsConcentradoHemaciasFiltradoEIrradiado: boolean,
    },
    rules: any[],
  },
  rsConcentradoPlaquetas: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      rsConcentradoPlaquetasAlteracaoFuncionalCirculacao: boolean,
      rsConcentradoPlaquetasAlteracaoFuncionalAnteagregante: boolean,
      rsConcentradoPlaquetasProcedimentosInvasivos: boolean,
      rsConcentradoPlaquetasCirurgiaPequenoPorte: boolean,
      rsConcentradoPlaquetasCirurgiaMedioPorte: boolean,
      rsConcentradoPlaquetasCirurgiaNeurologica: boolean,
      rsConcentradoPlaquetasEspecificar: boolean,
      rsConcentradoPlaquetasNaoAplica: boolean,
      rsConcentradoPlaquetasFiltrado: boolean,
      rsConcentradoPlaquetasFiltradoEIrradiado: boolean,
    },
    rules: any[],
  },
  rsPlasmaFrescoCongelado: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      rsPlasmaFrescoCongeladoCoagulopatia: boolean,
      rsPlasmaFrescoCongeladoEspecificar: boolean,
      rsPlasmaFrescoCongeladoNaoAplica: boolean,
    },
    rules: any[],
  },
  rsCrioprecipitado: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      rsCrioprecipitadoCoagulopatia: boolean,
      rsCrioprecipitadoEspecificar: boolean,
      rsCrioprecipitadoNaoAplica: boolean,
    },
    rules: any[],
  },
  mapaCirurgicoArea: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      areas: any[],
    },
    rules: any[],
  },
  suprimentosCotacao: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      naoExigeAnexoCotacao: boolean,
      flagCotacaoDefault: boolean,
      flagCotacaoParticularDefault: boolean,
      naoExigeValorDePacote: boolean,
    },
    rules: any[],
  },
  alterarSolicitacao: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      naoEditarProcedimentoPrincipal: boolean,
      naoEditarProcedimentosAssociados: boolean,
      naoEditarObservacaoReservaSala: boolean,
      naoEditarOpme: boolean,
      naoEditarEquipamentos: boolean,
      naoEditarTipoInternacao: boolean,
      naoEditarAnestesita: boolean,
      naoEditarEquipeMedica: boolean,
      naoEditarHonorarioAnestesista: boolean,
      naoEditarHonorarioMedico: boolean,
      naoEditarReservaHemocomponentes: boolean,
    },
    rules: any[],
  },
  guiaDeAutorizacao: {
    campos: {
      Cancelada: boolean,
      EmAnalise: boolean,
      AutorizadaParcial: boolean,
      AutorizadaAceitaMedico: boolean,
      Autorizada: boolean,
    },
    rules: any[],
  },
  observacaoDoCancelamento: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  centroCirurgico: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  tipoCentroCirurgico: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      Geral: boolean,
      Ambulatorial: boolean,
      HospitalDia: boolean,
      Hemodinamica: boolean,
      Radioterapia: boolean,
      SalaDePequenosProcedimentos: boolean,
    },
    rules: any[],
  },
  naoExibeMensagemTempoAutorizacaoPorTipoCentroCirurgico: {
    habilitado: boolean,
    campos: {
      Geral: boolean,
      Ambulatorial: boolean,
      HospitalDia: boolean,
      Hemodinamica: boolean,
      Radioterapia: boolean,
      SalaDePequenosProcedimentos: boolean,
    },
    rules: any[],
  }
  regiaoDaCirurgia: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  justificativaDoProtocolo: {
    habilitado: boolean,
    obrigatorio: boolean,
    rules: any[],
  },
  formularioMedico: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      limparReservaUtiNoTipoDeInternacaoAmbulatorial: boolean,
    },
    rules: [],
  },
  visualizarAgendamentoMedico: {
    habilitado: boolean,
    obrigatorio: boolean,
    campos: {
      permitirVisualizarAgendamentosDoMedicoNaTriagem: boolean,
    },
    rules: [],
  },
  anexo: {
    rules: [],
    acoes: {
      permiteGerarGuiaTissSadt: boolean,
    },
  },
  processarArquivoAnexo: {
    habilitado: boolean,
    campos: {
      processarImagem: boolean,
      processarPdf: boolean,
    },
    rules: any[],
  },
  horasDeAntecedenciaDeInternacao: {
    habilitado: false,
    obrigatorio: false,
    rules: [],
  },
}
